/**
 * Today's Almanac under the date (B91, option B of topics/b91-moons, approved by the owner 2026-10-26; it undoes the
 * part of D46 that moved the lines into the weather card): every day the moon line and the season line, then the
 * Almanac's other lines (a frost or freeze date that falls today with its link, the hottest or coldest stretch, the
 * wettest or driest month, the panel's Almanac rules with their tips). Muted 14px lines, 8px under the date and 2px
 * apart, no box, no tint. On the night a new or full moon is seen, and on the day of an equinox or solstice, that line
 * is in the main ink with its mark in gold (verdict-sun-page: the gold made for the page, 3:1 on it in every theme).
 *
 * Everything is the server's (the brief's almanac: sky.moon, sky.turn, calendar, rainMonth, rules; services/today
 * todayBriefFrom): the words, which night is "tonight", the moon's shape, how much is lit and on which side. The app draws
 * them and decides nothing. The todo_rule switches for the moon and the turn are the server's too (a line switched
 * off is not sent).
 *
 * Used by: TodayPage, under the date. Words (en/es): today.almanac (the list's label); every line is the brief's.
 *
 * @module TodayAlmanac
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Sun } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { MoonDisc } from '@/shared/components/icons/MoonDisc';
import type { TodayBrief } from '@/shared/api/todayBrief';

/** A line's words as a link, with the same light underline as the rest of Today. */
const LINE_LINK = 'rounded-sm underline decoration-[color-mix(in_srgb,currentColor_40%,transparent)] decoration-1 underline-offset-[5px] hover:decoration-current focus:outline-none focus-visible:decoration-current focus-visible:ring-2 focus-visible:ring-ring';
/** A line with an icon: the icon, then its words, top-aligned for a line that wraps. */
const ICON_ROW = 'flex items-start gap-2';
/** The icon's box, lined up with the first line of words. */
const ICON_BOX = 'mt-0.5 h-4 w-4 shrink-0';

/** What the Almanac draws, from the brief. */
interface TodayAlmanacProps {
  /** The brief's almanac. */
  almanac: TodayBrief['almanac'];
  /** Whether the wettest or driest month's line is said today (useRainMonth, the first days the page is opened in it). */
  rainShown: boolean;
}

/**
 * The Almanac's lines under the date; nothing when there is no line at all.
 *
 * @param props - TodayAlmanacProps.
 */
export const TodayAlmanac: React.FC<TodayAlmanacProps> = ({ almanac, rainShown }) => {
  const { reactTranslate: t } = useTranslation();
  const { moon, turn } = almanac.sky;
  const rainMonth = rainShown ? almanac.rainMonth : null;
  const shown = Boolean(moon || turn || almanac.calendar.length > 0 || rainMonth || almanac.rules.length > 0);
  if (!shown) return null;
  return (
    <ul className="mt-2 space-y-0.5 text-sm leading-snug text-muted-foreground" aria-label={t('today.almanac')}>
      {moon && (
        <li className={`${ICON_ROW} ${moon.tonight ? 'text-foreground' : ''}`}>
          {/* The moon as it looks tonight: the server's lit share and side; gold on the night a new or full moon is seen. */}
          <MoonDisc
            shape={moon.shape}
            lit={moon.lit}
            litSide={moon.litSide}
            className={`${ICON_BOX} ${moon.tonight ? 'text-verdict-sun-page' : 'text-muted-foreground'}`}
          />
          <span className="min-w-0">{moon.text}</span>
        </li>
      )}
      {turn && (
        <li className={`${ICON_ROW} ${turn.today ? 'text-foreground' : ''}`}>
          <Sun className={`${ICON_BOX} ${turn.today ? 'text-verdict-sun-page' : 'text-muted-foreground'}`} aria-hidden="true" />
          <span className="min-w-0">{turn.text}</span>
        </li>
      )}
      {almanac.calendar.map((line) => (
        <li key={line.key}>
          {/* A frost or freeze date leads to that season's dates on Weather. */}
          {line.link ? <Link to={line.link} className={LINE_LINK}>{line.text}</Link> : line.text}
        </li>
      ))}
      {rainMonth && <li>{rainMonth.text}</li>}
      {/* The panel's Almanac rules (Grow settings, Today), each with its advice under it. */}
      {almanac.rules.map((line) => (
        <li key={`rule-${line.id}`}>
          {line.text}
          {line.tip && <span className="block">{line.tip}</span>}
        </li>
      ))}
    </ul>
  );
};
